/**
 * Capacitor Native Features Service
 * Handles all mobile-specific functionality including:
 * - Push Notifications
 * - Camera Integration
 * - GPS/Location Services
 * - App State Management
 * - Network Status
 * - Storage
 * - Haptics
 * - Status Bar Control
 */

import { Capacitor, registerPlugin } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Geolocation } from '@capacitor/geolocation';
import { PushNotifications } from '@capacitor/push-notifications';
import { Network } from '@capacitor/network';
import { App as CapacitorApp } from '@capacitor/app';
import { Preferences } from '@capacitor/preferences';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { StatusBar, Style } from '@capacitor/status-bar';

// Background Geolocation (@capacitor-community/background-geolocation)
// This plugin ships only native code + TypeScript definitions, no JS implementation.
// We must register it manually via Capacitor's registerPlugin().
// On the web the proxy becomes a no-op stub which we guard against with isNative checks.
const BackgroundGeolocation = Capacitor.isNativePlatform()
  ? registerPlugin('BackgroundGeolocation')
  : null;

class CapacitorService {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.platform = Capacitor.getPlatform(); // 'ios', 'android', or 'web'
    this.listeners = {
      appState: [],
      network: [],
      location: [],
      pushNotification: []
    };
  }

  /**
   * Initialize all Capacitor features
   */
  async initialize() {
    if (!this.isNative) {
      console.log('📱 Running on web platform, skipping native initialization');
      return;
    }

    console.log('🚀 Initializing Capacitor Native Features...');
    console.log('📱 Platform:', this.platform);

    try {
      // Initialize status bar
      await this.initializeStatusBar();
      
      // Initialize app state listeners
      await this.initializeAppState();
      
      // Initialize network monitoring
      await this.initializeNetwork();
      
      console.log('✅ Capacitor initialization complete');
    } catch (error) {
      console.error('❌ Capacitor initialization error:', error);
    }
  }

  // ============================================================================
  // PUSH NOTIFICATIONS
  // ============================================================================

  /**
   * Initialize push notifications for mobile
   * @returns {Promise<string|null>} FCM token
   */
  async initializePushNotifications() {
    if (!this.isNative) {
      console.log('📱 Web platform, using Firebase web notifications');
      return null;
    }

    try {
      console.log('🔔 Initializing mobile push notifications...');

      // Request permission
      let permStatus = await PushNotifications.checkPermissions();

      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }

      if (permStatus.receive !== 'granted') {
        console.warn('⚠️ Push notification permission denied');
        return null;
      }

      // Register with OS
      await PushNotifications.register();

      // Listen for registration
      return new Promise((resolve) => {
        PushNotifications.addListener('registration', (token) => {
          console.log('✅ Push registration success, token:', token.value);
          resolve(token.value);
        });

        PushNotifications.addListener('registrationError', (error) => {
          console.error('❌ Push registration error:', error);
          resolve(null);
        });

        // Listen for incoming notifications (record delivery analytics)
        PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.log('📬 Push notification received:', notification);
          this._notifyListeners('pushNotification', notification);
          // Record delivery event
          const notifId = notification.data?.notification_id;
          if (notifId) {
            import('axios').then(({ default: axios }) => {
              axios.post('/notifications/delivery-event', {
                notification_id: notifId,
                event: 'delivered',
                platform: 'native',
              }).catch(() => {});
            });
          }
        });

        PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
          console.log('👆 Push notification action performed:', notification);
          this._notifyListeners('pushNotification', notification);
          // Record open event
          const notifId = notification.notification?.data?.notification_id;
          if (notifId) {
            import('axios').then(({ default: axios }) => {
              axios.post('/notifications/delivery-event', {
                notification_id: notifId,
                event: 'opened',
                platform: 'native',
              }).catch(() => {});
            });
          }
        });

        // Timeout after 10 seconds
        setTimeout(() => resolve(null), 10000);
      });
    } catch (error) {
      console.error('❌ Push notification initialization error:', error);
      return null;
    }
  }

  // ============================================================================
  // CAMERA
  // ============================================================================

  /**
   * Take a photo using device camera
   * @param {Object} options - Camera options
   * @returns {Promise<string>} Base64 image data
   */
  async takePicture(options = {}) {
    try {
      const image = await Camera.getPhoto({
        quality: options.quality || 90,
        allowEditing: options.allowEditing || false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Camera,
        ...options
      });

      return `data:image/${image.format};base64,${image.base64String}`;
    } catch (error) {
      console.error('❌ Camera error:', error);
      throw error;
    }
  }

  /**
   * Pick photo from gallery
   * @param {Object} options - Camera options
   * @returns {Promise<string>} Base64 image data
   */
  async pickPhoto(options = {}) {
    try {
      const image = await Camera.getPhoto({
        quality: options.quality || 90,
        allowEditing: options.allowEditing || false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Photos,
        ...options
      });

      return `data:image/${image.format};base64,${image.base64String}`;
    } catch (error) {
      console.error('❌ Photo picker error:', error);
      throw error;
    }
  }

  /**
   * Pick multiple photos from gallery
   * @returns {Promise<Array>} Array of photo objects
   */
  async pickMultiplePhotos() {
    try {
      const result = await Camera.pickImages({
        quality: 90,
      });

      return result.photos.map(photo => ({
        path: photo.path,
        webPath: photo.webPath,
      }));
    } catch (error) {
      console.error('❌ Multiple photo picker error:', error);
      throw error;
    }
  }

  // ============================================================================
  // GEOLOCATION
  // ============================================================================

  /**
   * Get current position
   * @returns {Promise<Object>} Position coordinates
   */
  async getCurrentPosition() {
    try {
      const coordinates = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000
      });

      return {
        latitude: coordinates.coords.latitude,
        longitude: coordinates.coords.longitude,
        accuracy: coordinates.coords.accuracy,
        altitude: coordinates.coords.altitude,
        speed: coordinates.coords.speed,
        heading: coordinates.coords.heading,
        timestamp: coordinates.timestamp
      };
    } catch (error) {
      console.error('❌ Geolocation error:', error);
      throw error;
    }
  }

  /**
   * Watch position changes
   * @param {Function} callback - Called when position changes
   * @returns {string} Watch ID
   */
  async watchPosition(callback) {
    try {
      const watchId = await Geolocation.watchPosition(
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        },
        (position, err) => {
          if (err) {
            console.error('❌ Position watch error:', err);
            return;
          }

          callback({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp
          });
        }
      );

      return watchId;
    } catch (error) {
      console.error('❌ Watch position error:', error);
      throw error;
    }
  }

  /**
   * Clear position watch
   * @param {string} watchId - Watch ID to clear
   */
  async clearWatch(watchId) {
    try {
      await Geolocation.clearWatch({ id: watchId });
    } catch (error) {
      console.error('❌ Clear watch error:', error);
    }
  }

  /**
   * Check location permissions
   * @returns {Promise<string>} Permission status
   */
  async checkLocationPermissions() {
    try {
      const permissions = await Geolocation.checkPermissions();
      return permissions.location;
    } catch (error) {
      console.error('❌ Check permissions error:', error);
      return 'denied';
    }
  }

  /**
   * Request location permissions
   * @returns {Promise<string>} Permission status
   */
  async requestLocationPermissions() {
    try {
      const permissions = await Geolocation.requestPermissions();
      return permissions.location;
    } catch (error) {
      console.error('❌ Request permissions error:', error);
      return 'denied';
    }
  }

  // ============================================================================
  // BACKGROUND GEOLOCATION
  // ============================================================================

  /**
   * Start background location tracking
   * @param {Function} callback - Called when location updates
   */
  async startBackgroundGeolocation(callback) {
    if (!this.isNative) {
      console.warn('⚠️ Background geolocation only available on native platforms');
      return;
    }

    if (!BackgroundGeolocation) {
      console.error('❌ Background Geolocation plugin not registered');
      throw new Error('Background Geolocation plugin not available on this platform');
    }

    try {
      // Add watcher for background location
      const watcherId = await BackgroundGeolocation.addWatcher(
        {
          backgroundMessage: "Tracking your trip location",
          backgroundTitle: "RoamMate Location",
          requestPermissions: true,
          stale: false,
          distanceFilter: 10 // meters
        },
        (location, error) => {
          if (error) {
            console.error('❌ Background location error:', error);
            return;
          }

          if (location) {
            console.log('📍 Background location update:', location);
            callback({
              latitude: location.latitude,
              longitude: location.longitude,
              accuracy: location.accuracy,
              speed: location.speed,
              bearing: location.bearing,
              altitude: location.altitude,
              time: location.time
            });
          }
        }
      );

      console.log('✅ Background geolocation started, watcher ID:', watcherId);
      return watcherId;
    } catch (error) {
      console.error('❌ Start background geolocation error:', error);
      throw error;
    }
  }

  /**
   * Stop background location tracking
   * @param {string} watcherId - Watcher ID to remove
   */
  async stopBackgroundGeolocation(watcherId) {
    if (!this.isNative) return;

    if (!BackgroundGeolocation) {
      console.warn('⚠️ Background Geolocation plugin not available');
      return;
    }

    try {
      await BackgroundGeolocation.removeWatcher({ id: watcherId });
      console.log('✅ Background geolocation stopped');
    } catch (error) {
      console.error('❌ Stop background geolocation error:', error);
    }
  }

  // ============================================================================
  // APP STATE MANAGEMENT
  // ============================================================================

  /**
   * Initialize app state listeners
   */
  async initializeAppState() {
    if (!this.isNative) return;

    CapacitorApp.addListener('appStateChange', (state) => {
      console.log('📱 App state changed:', state.isActive ? 'active' : 'background');
      this._notifyListeners('appState', state);
    });

    CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      console.log('⬅️ Back button pressed, can go back:', canGoBack);
      if (!canGoBack) {
        CapacitorApp.exitApp();
      } else {
        window.history.back();
      }
    });
  }

  /**
   * Subscribe to app state changes
   * @param {Function} callback - Called when app state changes
   */
  onAppStateChange(callback) {
    this.listeners.appState.push(callback);
  }

  /**
   * Exit the app
   */
  async exitApp() {
    if (!this.isNative) return;
    await CapacitorApp.exitApp();
  }

  // ============================================================================
  // NETWORK STATUS
  // ============================================================================

  /**
   * Initialize network monitoring
   */
  async initializeNetwork() {
    if (!this.isNative) return;

    Network.addListener('networkStatusChange', (status) => {
      console.log('🌐 Network status changed:', status);
      this._notifyListeners('network', status);
    });

    const status = await Network.getStatus();
    console.log('🌐 Initial network status:', status);
  }

  /**
   * Get current network status
   * @returns {Promise<Object>} Network status
   */
  async getNetworkStatus() {
    try {
      return await Network.getStatus();
    } catch (error) {
      console.error('❌ Get network status error:', error);
      return { connected: true, connectionType: 'unknown' };
    }
  }

  /**
   * Subscribe to network status changes
   * @param {Function} callback - Called when network status changes
   */
  onNetworkChange(callback) {
    this.listeners.network.push(callback);
  }

  // ============================================================================
  // STORAGE (Preferences)
  // ============================================================================

  /**
   * Set a value in native storage
   * @param {string} key - Storage key
   * @param {any} value - Value to store
   */
  async setPreference(key, value) {
    try {
      await Preferences.set({
        key,
        value: typeof value === 'string' ? value : JSON.stringify(value)
      });
    } catch (error) {
      console.error('❌ Set preference error:', error);
    }
  }

  /**
   * Get a value from native storage
   * @param {string} key - Storage key
   * @returns {Promise<any>} Retrieved value
   */
  async getPreference(key) {
    try {
      const { value } = await Preferences.get({ key });
      
      // Try to parse as JSON
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    } catch (error) {
      console.error('❌ Get preference error:', error);
      return null;
    }
  }

  /**
   * Remove a value from native storage
   * @param {string} key - Storage key
   */
  async removePreference(key) {
    try {
      await Preferences.remove({ key });
    } catch (error) {
      console.error('❌ Remove preference error:', error);
    }
  }

  /**
   * Clear all preferences
   */
  async clearPreferences() {
    try {
      await Preferences.clear();
    } catch (error) {
      console.error('❌ Clear preferences error:', error);
    }
  }

  // ============================================================================
  // HAPTICS (Vibration)
  // ============================================================================

  /**
   * Light haptic feedback
   */
  async hapticsLight() {
    if (!this.isNative) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch (error) {
      console.error('❌ Haptics error:', error);
    }
  }

  /**
   * Medium haptic feedback
   */
  async hapticsMedium() {
    if (!this.isNative) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Medium });
    } catch (error) {
      console.error('❌ Haptics error:', error);
    }
  }

  /**
   * Heavy haptic feedback
   */
  async hapticsHeavy() {
    if (!this.isNative) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Heavy });
    } catch (error) {
      console.error('❌ Haptics error:', error);
    }
  }

  /**
   * Vibrate device
   * @param {number} duration - Duration in milliseconds
   */
  async vibrate(duration = 300) {
    if (!this.isNative) return;
    try {
      await Haptics.vibrate({ duration });
    } catch (error) {
      console.error('❌ Vibrate error:', error);
    }
  }

  /**
   * Success haptic feedback
   */
  async hapticsNotification(type = 'SUCCESS') {
    if (!this.isNative) return;
    try {
      await Haptics.notification({ type });
    } catch (error) {
      console.error('❌ Haptics error:', error);
    }
  }

  // ============================================================================
  // STATUS BAR CONTROL
  // ============================================================================

  /**
   * Initialize status bar
   */
  async initializeStatusBar() {
    if (!this.isNative) return;

    try {
      // Set status bar style
      await StatusBar.setStyle({ style: Style.Dark });
      
      // Set status bar background color (Android only)
      if (this.platform === 'android') {
        await StatusBar.setBackgroundColor({ color: '#10b981' }); // emerald-500
      }

      console.log('✅ Status bar initialized');
    } catch (error) {
      console.error('❌ Status bar initialization error:', error);
    }
  }

  /**
   * Show status bar
   */
  async showStatusBar() {
    if (!this.isNative) return;
    try {
      await StatusBar.show();
    } catch (error) {
      console.error('❌ Show status bar error:', error);
    }
  }

  /**
   * Hide status bar
   */
  async hideStatusBar() {
    if (!this.isNative) return;
    try {
      await StatusBar.hide();
    } catch (error) {
      console.error('❌ Hide status bar error:', error);
    }
  }

  /**
   * Set status bar style
   * @param {string} style - 'Light' or 'Dark'
   */
  async setStatusBarStyle(style = 'Dark') {
    if (!this.isNative) return;
    try {
      await StatusBar.setStyle({ style: style === 'Light' ? Style.Light : Style.Dark });
    } catch (error) {
      console.error('❌ Set status bar style error:', error);
    }
  }

  /**
   * Set status bar background color (Android only)
   * @param {string} color - Hex color code
   */
  async setStatusBarColor(color) {
    if (!this.isNative || this.platform !== 'android') return;
    try {
      await StatusBar.setBackgroundColor({ color });
    } catch (error) {
      console.error('❌ Set status bar color error:', error);
    }
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Check if running on native platform
   * @returns {boolean}
   */
  isNativePlatform() {
    return this.isNative;
  }

  /**
   * Get platform
   * @returns {string} 'ios', 'android', or 'web'
   */
  getPlatform() {
    return this.platform;
  }

  /**
   * Notify event listeners
   * @private
   */
  _notifyListeners(type, data) {
    if (this.listeners[type]) {
      this.listeners[type].forEach(callback => callback(data));
    }
  }

  /**
   * Clean up all listeners
   */
  async cleanup() {
    if (!this.isNative) return;

    try {
      await CapacitorApp.removeAllListeners();
      await Network.removeAllListeners();
      await PushNotifications.removeAllListeners();
      console.log('✅ Capacitor listeners cleaned up');
    } catch (error) {
      console.error('❌ Cleanup error:', error);
    }
  }
}

// Export singleton instance
export default new CapacitorService();
